import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/api';
import loadImage from '../assets/imageLoader';
import { FaUser, FaArrowLeft, FaMapMarkerAlt, FaEye, FaCheckCircle, FaRegCircle } from 'react-icons/fa';
import InfoModal from '../components/InfoModal';

function ViewApplicantsPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', message: '' });

  const showInfoModal = (title, message) => {
    setModalContent({ title, message });
    setIsInfoModalOpen(true);
  };

  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [initialSelectedApplicants, setInitialSelectedApplicants] = useState([]);

  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        const response = await apiClient.get(`/jobs/${jobId}`);
        setJob(response.data);
        const currentSelections = response.data.selected_applicants?.map(sa => sa.applicant_profile_id) || [];
        setSelectedApplicants(currentSelections);
        setInitialSelectedApplicants(currentSelections);
      } catch (err) {
        setError('Failed to load applicants.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchJobDetails();
  }, [jobId]);

  const handleToggleSelection = (applicantId) => {
    setSelectedApplicants(prev => {
      if (prev.includes(applicantId)) {
        return prev.filter(id => id !== applicantId);
      } else {
        return [...prev, applicantId];
      }
    });
  };

  const handleConfirmSelection = async () => {
    try {
      await apiClient.put(`/jobs/${jobId}/select-applicants`, { selectedApplicantIds: selectedApplicants });
      setIsSelectionMode(false);
      setInitialSelectedApplicants(selectedApplicants);
      showInfoModal('Success', 'Selection updated successfully!');
    } catch (err) {
      console.error('Failed to update selection:', err);
      showInfoModal('Error', 'Failed to update selection.');
    }
  };

  const handleCancelSelection = () => {
    setIsSelectionMode(false);
    setSelectedApplicants(initialSelectedApplicants);
  };

  if (loading) return <div className="flex justify-center items-center h-screen">Loading...</div>;
  if (error) return <div className="text-center text-red-500 mt-10">{error}</div>;

  const applicants = job?.details?.[0]?.applications || [];
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentApplicants = applicants.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(applicants.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="max-w-6xl mx-auto p-6">
      <InfoModal 
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
        title={modalContent.title}
        message={modalContent.message}
      />
      <button onClick={() => navigate('/owner-dashboard', { state: { activeTab: 'published' } })} className="flex items-center text-blue-600 hover:underline mb-6">
        <FaArrowLeft className="mr-2" />
        Back to Published Jobs
      </button>

      <div className="flex justify-between items-center mb-6">
        <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Applicants for "{job?.title}"</h1>
            <p className="text-gray-600">Total Applicants: {applicants.length}</p>
        </div>
        
        {job?.post_model === 'direct_apply' && (
            <div>
                {!isSelectionMode ? (
                    <button 
                        onClick={() => setIsSelectionMode(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg flex items-center"
                    >
                        Select/Edit Applicant
                    </button>
                ) : (
                    <div className="flex space-x-2">
                        <button onClick={handleCancelSelection} className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 px-4 rounded-lg">Cancel</button>
                        <button onClick={handleConfirmSelection} className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg">Confirm Selection</button>
                    </div>
                )}
            </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Applicant</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentApplicants.length > 0 ? (
                currentApplicants.map((app) => {
                    const isSelected = selectedApplicants.includes(app.applicant?.id);
                    return (
                        <tr key={app.id} className={`hover:bg-gray-50 ${isSelected && !isSelectionMode ? 'bg-green-50' : ''}`}>
                            <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                                {isSelectionMode && (
                                    <button onClick={() => handleToggleSelection(app.applicant?.id)} className="mr-4 text-gray-400 hover:text-blue-600 focus:outline-none">
                                        {isSelected ? <FaCheckCircle className="text-blue-600 text-xl" /> : <FaRegCircle className="text-xl" />}
                                    </button>
                                )}
                                {!isSelectionMode && isSelected && <FaCheckCircle className="text-green-500 mr-4 text-xl" title="Selected" />}

                                <div className="flex-shrink-0 h-10 w-10">
                                {app.applicant?.picture ? (
                                    <img className="h-10 w-10 rounded-full object-cover" src={loadImage(app.applicant.picture)} alt="" />
                                ) : (
                                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500"><FaUser /></div>
                                )}
                                </div>
                                <div className="ml-4"><div className="text-sm font-medium text-gray-900">{app.applicant?.name || 'Unknown Name'}</div></div>
                            </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"><FaMapMarkerAlt className="inline mr-1" /> {app.applicant?.location?.district || 'N/A'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button onClick={() => navigate(`/applicant-details/${app.applicant?.id}`)} className="text-blue-600 hover:text-blue-900 flex items-center justify-end ml-auto"><FaEye className="mr-1" /> Details</button>
                            </td>
                        </tr>
                    );
                })
              ) : (
                <tr><td colSpan="3" className="px-6 py-4 text-center text-gray-500">No applicants found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {applicants.length > itemsPerPage && (
        <div className="flex justify-center mt-8">
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
            <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className="px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50">Previous</button>
            {[...Array(totalPages)].map((_, i) => (
              <button key={i} onClick={() => paginate(i + 1)} className={`px-4 py-2 border text-sm font-medium ${currentPage === i + 1 ? 'bg-blue-50 border-blue-500 text-blue-600' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>{i + 1}</button>
            ))}
            <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} className="px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50">Next</button>
          </nav>
        </div>
      )}
    </div>
  );
}

export default ViewApplicantsPage;
